import { NavigationEnd, Router, RouterEvent } from "@angular/router";
import { Subscription } from "rxjs";
import {
  SimpleChange,
  EventEmitter,
  HostListener,
  ElementRef,
  Component,
  OnDestroy,
  ViewChild,
  Output,
  Input,
} from "@angular/core";

// Configurations
import { mixPanelEventsConfigurations } from "src/app/configurations/main.configurations";

// Libraries
import * as _ from "underscore";

// Pipes
import { GamesFilterPipe } from "src/app/modules/shared/pipes/games-filter.pipe";

// Models
import { FilteredGamesBy } from "src/app/modules/game-groups/models/games/filtered-games-by.model";
import { TrackGameEvent } from "src/app/modules/shared/models/mix-panel/track-game-event.model";
import { GamePregmatic } from "src/app/modules/game-groups/models/game.model";

// Services
import { GameGroupsService } from "src/app/modules/game-groups/services/game-groups.service";
import { TranslationService } from "src/app/modules/shared/services/translation.service";
import { MixPanelService } from "src/app/modules/shared/services/mix-panel.service";
import { CommonService } from "src/app/modules/shared/services/common.service";
import { UtilityService } from "src/app/utility/utility.service";

@Component({
  selector: "app-recent-search-games",
  templateUrl: "./recent-search-games.component.html",
  styleUrls: ["./recent-search-games.component.scss"],
})
export class RecentSearchGamesComponent implements OnDestroy {
  // View Child
  @ViewChild("searchInput", { static: false }) searchInput: ElementRef;

  // Output
  @Output() closeSearchResults: EventEmitter<void> = new EventEmitter<void>();

  // Inputs
  @Input() callingFrom: string = "";

  // Numbers
  searchResultsDefaultSize: number = 18;
  loadMoreSearchGames: number = this.searchResultsDefaultSize;

  // Strings
  languageCode: string = "";
  searchText: string = "";

  // Booleans
  isLiveCasinoPage: boolean = false;
  isGamePlayWindow: boolean = false;
  enableSearching: boolean = false;
  isLoggedIn: boolean = false;
  isLoading: boolean = false;

  // Arrays
  totalSearchedGamesList: GamePregmatic[] = [];
  filteredGamesList: GamePregmatic[] = [];
  gamesList: GamePregmatic[] = [];

  // Enums
  windowType: "device" | "mobile" = "device";

  // Timeout
  timeoutInstance: NodeJS.Timer;

  // Subscriptions
  subscriptions: Subscription[] = [];

  constructor(
    private translationService: TranslationService,
    private gameGroupsService: GameGroupsService,
    private gamesFilterPipe: GamesFilterPipe,
    private mixPanelService: MixPanelService,
    private utilityService: UtilityService,
    private commonService: CommonService,
    private router: Router
  ) {}

  // -----------------------------------------------------------------
  // Lifecycle Hooks
  ngOnInit(): void {
    this.getWindowType();

    this.onAppendGamePlayClass();

    this.isLoggedIn = this.utilityService.isUserLoggedIn();

    this.subscriptions = [
      this.translationService.langCodeSb$.subscribe((languageCode: string) => {
        this.languageCode = languageCode;
      }),
      this.commonService.loginComplete$.subscribe((isLoggedIn: boolean) => {
        this.isLoggedIn = isLoggedIn;

        this.onGetLobbyGroupsAndGames(true);
      }),
      this.commonService.logOutComplete$.subscribe(() => {
        this.isLoggedIn = false;

        this.onGetLobbyGroupsAndGames(true);
      }),
      this.commonService.isLiveCasinoPage$.subscribe(
        (isLiveCasinoPage: boolean) => {
          this.isLiveCasinoPage = isLiveCasinoPage;
        }
      ),
      this.router.events.subscribe((event: RouterEvent) => {
        // add class name on search bar when its in GamePlay screen
        if (event instanceof NavigationEnd) {
          this.onAppendGamePlayClass();
        }
      }),
    ];
  }

  ngOnChanges(changes: { [propName: string]: SimpleChange }): void {
    if (
      changes["callingFrom"] &&
      changes["callingFrom"].previousValue !==
        changes["callingFrom"].currentValue
    ) {
      this.callingFrom = changes["callingFrom"].currentValue;
    }
  }

  ngAfterViewInit(): void {
    if (this.windowType === "mobile") {
      this.searchInput.nativeElement.focus();
    }
  }

  // -----------------------------------------------------------------
  // Host Listeners
  @HostListener("window:resize") onResize(): void {
    this.getWindowType();
  }

  @HostListener("window:orientationchange") onRotate(): void {
    this.getWindowType();
  }

  // -----------------------------------------------------------------
  // Get Window Type
  getWindowType(): void {
    let clientWidth: number = document.body.clientWidth;

    if (clientWidth <= 1023) {
      this.windowType = "mobile";
    } else {
      this.windowType = "device";
    }

    /**
     * Default search result will changes based on device
     * resolution so here we update the search result default
     * size to render..
     */
    if (clientWidth >= 768 && clientWidth <= 1023) {
      this.searchResultsDefaultSize = 25;

      this.loadMoreSearchGames = this.searchResultsDefaultSize;

      if (this.searchText && this.searchText.length > 0) {
        this.onSearchGames(this.searchText);
      }
    } else {
      this.searchResultsDefaultSize = 18;

      this.loadMoreSearchGames = this.searchResultsDefaultSize;

      if (this.searchText && this.searchText.length > 0) {
        this.onSearchGames(this.searchText);
      }
    }
  }

  // -----------------------------------------------------------------
  // Set Methods
  onAppendGamePlayClass(): void {
    if (
      window.location.pathname &&
      window.location.pathname.includes(
        `/${this.translationService.instant("url.game")}`
      )
    ) {
      this.isGamePlayWindow = true;
    } else {
      this.isGamePlayWindow = false;
    }
  }

  onGetLobbyGroupsAndGames(isForce?: boolean): void {
    this.isLoading = true;

    Promise.resolve(this.gameGroupsService.getGameGroupGamesp(isForce)).then(
      (gamesList: GamePregmatic[]) => {
        if (gamesList && gamesList.length > 0) {
          this.gamesList = JSON.parse(JSON.stringify(gamesList));
        } else {
          this.filteredGamesList = [];
        }

        this.isLoading = false;
      }
    );
  }

  onSearchGames(searchKey: string): void {
    this.isLoading = true;

    Promise.resolve(this.gameGroupsService.getGameGroupGamesp()).then(
      (gamesList: GamePregmatic[]) => {
        if (gamesList && gamesList.length > 0) {
          this.gamesList = JSON.parse(JSON.stringify(gamesList));

          let filterData: FilteredGamesBy = {};

          if (this.callingFrom === "liveCasinoLobby") {
            filterData = { typeOfGames: "live-game" };
          }

          this.gamesList =
            Object.keys(filterData).length > 0
              ? this.gamesFilterPipe.transform(this.gamesList, filterData)
              : this.gamesList;
        } else {
          this.filteredGamesList = [];
        }

        this.isLoading = false;

        this.searchText = searchKey;

        if (searchKey && searchKey.length > 0) {
          this.loadMoreSearchGames = this.searchResultsDefaultSize;

          this.totalSearchedGamesList = undefined;
          /**
           * User can seach game not only based on name, he can search based on game
           * tag name & vendor display name (means provider name)
           */
          this.totalSearchedGamesList = _.filter(
            this.gamesList,
            (game: GamePregmatic) => {
              return (
                game.name.toLowerCase().indexOf(searchKey.toLowerCase()) !=
                  -1 ||
                game.vendorDisplayName
                  .toLowerCase()
                  .indexOf(searchKey.toLowerCase()) != -1 ||
                (game.tagList &&
                  game.tagList
                    .join("")
                    .toLowerCase()
                    .indexOf(searchKey.toLowerCase()) != -1)
              );
            }
          );

          this.filteredGamesList = this.totalSearchedGamesList.slice(
            0,
            this.searchResultsDefaultSize
          );
        } else {
          this.filteredGamesList = [];
        }
      }
    );

    this.onSendMixPanelEvent(searchKey);
  }

  /*
    Search loading related functionality
    case 1:
    - Basically we load only 18 games on page load
      when user scroll we will keep on adding 18(searchResultsDefaultSize) more games on each scroll..
    case 2:
    - But only in ipad the case is different we load 25 games on page load
      & we are handling this DefaultSize for different resolutions in getWindowType()...
  */
  onLoadMoreSearchResult(event): void {
    if (
      event.srcElement.classList &&
      event.srcElement.classList.contains("searchresults-card")
    ) {
      let searchWrapper: HTMLElement = document.getElementById(
        "searchresults-card"
      );

      if (
        searchWrapper.scrollTop + searchWrapper.offsetHeight >=
        searchWrapper.scrollHeight - 50
      ) {
        this.loadMoreSearchGames += this.searchResultsDefaultSize;

        this.filteredGamesList = this.totalSearchedGamesList.slice(
          0,
          this.loadMoreSearchGames
        );
      }
    }
  }

  onCloseSearchResultsHandler(): void {
    this.searchInput.nativeElement.value = "";

    this.onSearchGames("");

    if (this.enableSearching) {
      this.onStopSearching();
    }
  }

  onFocusInputText(): void {
    setTimeout(() => {
      if (document.getElementById("searchtext")) {
        document.getElementById("searchtext").focus();
      }
    });
  }

  onStartSearching(): void {
    this.enableSearching = true;
  }

  onStopSearching(): void {
    this.enableSearching = false;
  }

  onSendMixPanelEvent(searchValue: string): void {
    if (this.timeoutInstance) {
      clearTimeout(this.timeoutInstance);
    }

    this.timeoutInstance = setTimeout(() => {
      if (searchValue) {
        let evenProperties: TrackGameEvent = {
          query: searchValue,
          vertical: "Header",
        };

        if (this.callingFrom === "casinoLobby") {
          evenProperties.vertical = "Casino";
        } else if (this.callingFrom === "liveCasinoLobby") {
          evenProperties.vertical = "Live Casino";
        }

        this.mixPanelService.trackMixpanelEvents(
          mixPanelEventsConfigurations.initiated_Search,
          evenProperties
        );
      }
    }, 2000);
  }

  // -----------------------------------------------------------------
  // On Destroy
  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription: Subscription) =>
      subscription.unsubscribe()
    );
  }
}
